import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

/**
 * Top-headlines widget — pulls from /api/news/headlines, refreshes every
 * 5 minutes, and again whenever the tab regains focus or visibility.
 * US / World tabs and a manual refresh button.
 */
const NewsWidget = () => {
  const [news, setNews] = useState({ us: [], world: [], fetchedAt: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("us");

  const fetchNews = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    try {
      // Cache-bust so we hit the backend (and NewsAPI) on each call
      const response = await axios.get(`${API}/news/headlines`, {
        params: { _t: Date.now() },
        withCredentials: true,
      });
      setNews(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching news:", err);
      setError("Unable to load news");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
    // Real-time refresh: every 5 minutes while the tab is open
    const interval = setInterval(() => fetchNews({ silent: true }), 5 * 60 * 1000);

    // Also refetch when the window regains focus / tab becomes visible
    const onFocus = () => fetchNews({ silent: true });
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchNews({ silent: true });
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchNews]);

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const lastUpdatedLabel = news.fetchedAt ? formatTime(news.fetchedAt) : null;
  const articles = activeTab === "us" ? news.us : news.world;

  return (
    <Card className="bento-card" data-testid="news-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1m2 13a2 2 0 0 1-2-2V7m2 13a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            Today's Headlines
            {lastUpdatedLabel && (
              <span
                className="text-[11px] font-normal text-muted-foreground ml-1"
                data-testid="news-last-updated"
                title={news.fetchedAt ? new Date(news.fetchedAt).toLocaleString() : ""}
              >
                · Updated {lastUpdatedLabel}
              </span>
            )}
          </CardTitle>
          <div className="flex gap-1 items-center">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => fetchNews({ silent: true })}
              className="h-7 w-7 p-0"
              disabled={refreshing}
              data-testid="news-refresh-btn"
              title="Refresh now"
            >
              <Loader2 className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button
              size="sm"
              variant={activeTab === "us" ? "default" : "ghost"}
              onClick={() => setActiveTab("us")}
              className="h-7 px-2 text-xs"
              data-testid="news-tab-us"
            >
              🇺🇸 US
            </Button>
            <Button
              size="sm"
              variant={activeTab === "world" ? "default" : "ghost"}
              onClick={() => setActiveTab("world")}
              className="h-7 px-2 text-xs"
              data-testid="news-tab-world"
            >
              🌍 World
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-6 text-muted-foreground text-sm">{error}</div>
        ) : articles.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm">No headlines available</div>
        ) : (
          <div className="space-y-3">
            {articles.slice(0, 5).map((article, idx) => (
              <a
                key={idx}
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-3 rounded-lg bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors border border-transparent hover:border-zinc-700"
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-2 text-foreground">{article.title}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-muted-foreground">{article.source}</span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">{formatTime(article.publishedAt)}</span>
                    </div>
                  </div>
                  <svg
                    className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
                  </svg>
                </div>
              </a>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NewsWidget;
